import { createContext, useContext, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';
import {
  renderCaptioned, renderComposited, renderTimeline, exportPlain, transcodeToMp4, extractAudio,
} from '../lib/videoRender';
import { PostVideoDialog } from '../components/PostVideoDialog';

const Ctx = createContext(null);
export const useVideoRender = () => useContext(Ctx);

const uid = () => Math.random().toString(36).slice(2, 9);

/** Runs video render+upload jobs in the background so users aren't blocked by
 *  the in-browser render. Renders use detached DOM elements + blobs captured
 *  before the modal closed, so they survive navigation within the SPA. */
export const VideoRenderProvider = ({ children }) => {
  const { api } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [postNowProject, setPostNowProject] = useState(null);
  const queueRef = useRef([]);
  const runningRef = useRef(false);

  const setMsg = (id, msg) => setJobs((j) => j.map((x) => (x.id === id ? { ...x, msg } : x)));

  const uploadSource = async (projectId, blob, ext, duration) => {
    const fd = new FormData();
    fd.append('file', new File([blob], `clip.${ext}`, { type: blob.type }));
    fd.append('kind', 'upload');
    if (duration) fd.append('duration', String(duration));
    const r = await api.post(`/video/projects/${projectId}/source`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data.media;
  };

  const produceMedia = async (job, onProgress) => {
    if (job.kind === 'timeline') {
      let webm = await renderTimeline({ clips: job.clips, broll: job.broll || [], format: job.format, onProgress });
      // Optionally caption the assembled video (transcribe its audio, then burn in).
      if (job.captions) {
        try {
          onProgress('Transcribing audio…');
          const audio = await extractAudio(webm);
          const fd = new FormData();
          fd.append('file', new File([audio], 'audio.m4a', { type: 'audio/mp4' }));
          const tr = await api.post(`/video/projects/${job.projectId}/transcribe`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const words = tr.data.words || [];
          if (words.length) {
            onProgress('Burning captions…');
            webm = await renderCaptioned({ srcBlob: webm, words, presetKey: job.presetKey, format: job.format });
          }
        } catch (_) { /* caption step is best-effort; keep the assembled video */ }
      }
      let media;
      try { onProgress('Finalizing MP4…'); media = await uploadSource(job.projectId, await transcodeToMp4(webm), 'mp4', job.totalDuration); }
      catch (_) { onProgress('Uploading…'); media = await uploadSource(job.projectId, webm, 'webm', job.totalDuration); }
      media.format = job.format;
      if (job.captions) media.captioned = true;
      return media;
    }
    // single clip
    // When editing an existing project the original is already saved; only
    // upload the source for fresh recordings/uploads (no srcUrl).
    if (!job.srcUrl) {
      onProgress('Saving original…');
      await uploadSource(job.projectId, job.srcBlob, 'webm', job.clipDuration);
    }
    let media;
    const ov = job.overlays || {};
    const hasPhase4 = !!(ov.logo?.enabled || ov.outro?.enabled || ov.lowerThird?.enabled || ov.progressBar?.enabled || job.musicUrl || job.voiceoverUrl);
    if (job.captioned || hasPhase4) {
      const webm = await renderComposited({
        srcBlob: job.srcBlob,
        srcUrl: job.srcUrl || null,
        words: job.captioned ? job.words : null,
        presetKey: job.presetKey,
        format: job.format,
        trimStart: job.trimStart, trimEnd: job.trimEnd, clipDuration: job.clipDuration,
        overlays: ov,
        musicUrl: job.musicUrl || null, musicVolume: job.musicVolume,
        duckMain: job.duckMain, voiceoverUrl: job.voiceoverUrl || null,
        onProgress,
      });
      try { onProgress('Finalizing MP4…'); media = await uploadSource(job.projectId, await transcodeToMp4(webm), 'mp4', job.clipDuration); }
      catch (_) { onProgress('Uploading…'); media = await uploadSource(job.projectId, webm, 'webm', job.clipDuration); }
    } else {
      try { onProgress('Rendering…'); media = await uploadSource(job.projectId, await exportPlain(job), 'mp4', job.clipDuration); }
      catch (_) { onProgress('Uploading…'); media = await uploadSource(job.projectId, job.srcBlob, 'webm', job.clipDuration); }
    }
    media.format = job.format;
    if (job.captioned) media.captioned = true;
    if (hasPhase4) media.branded = true;
    return media;
  };

  const runJob = async (job) => {
    const onProgress = (msg) => setMsg(job.id, msg);
    try {
      if (job.captioned && job.words?.length) {
        try { await api.put(`/video/projects/${job.projectId}/captions`, { preset: job.presetKey, words: job.words, enabled: true }); } catch (_) { /* non-critical */ }
      }
      const media = await produceMedia(job, onProgress);
      onProgress('Saving to library…');
      await api.post(`/video/projects/${job.projectId}/finalize`, { media });
      if (job.targetPostId) {
        await api.post(`/video/projects/${job.projectId}/attach`, { media, campaign_post_id: job.targetPostId });
      }
      // mark done briefly then remove
      setJobs((j) => j.map((x) => (x.id === job.id ? { ...x, msg: 'Done', done: true } : x)));
      toast.success(job.targetPostId ? `${job.label} — saved & attached` : `${job.label} — saved to library`);
      window.dispatchEvent(new CustomEvent('video-render-complete', { detail: { projectId: job.projectId } }));
      // "Save & post now": open the platform picker once the render is ready.
      if (job.postNowAfter) {
        try {
          const r = await api.get(`/video/projects/${job.projectId}`);
          setPostNowProject(r.data);
        } catch (_) { /* project fetch best-effort */ }
      }
      setTimeout(() => setJobs((j) => j.filter((x) => x.id !== job.id)), 2500);
    } catch (e) {
      console.error('[video-render] job failed', job?.label, e);
      setJobs((j) => j.filter((x) => x.id !== job.id));
      toast.error(`${job.label} failed — ${e?.message || 'please try again'}`);
    }
  };

  const pump = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    while (queueRef.current.length) {
      const job = queueRef.current.shift();
      // eslint-disable-next-line no-await-in-loop
      await runJob(job);
    }
    runningRef.current = false;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enqueue = useCallback((job) => {
    const id = uid();
    const full = { ...job, id, msg: 'Queued…' };
    setJobs((j) => [...j, { id, label: job.label || 'Rendering video', msg: 'Queued…' }]);
    queueRef.current.push(full);
    pump();
  }, [pump]);

  return (
    <Ctx.Provider value={{ enqueue }}>
      {children}
      {jobs.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[100] space-y-2 w-72" data-testid="video-render-tray">
          {jobs.map((j) => (
            <div key={j.id} className="rounded-lg border border-zinc-200 bg-white shadow-lg p-3 flex items-start gap-2" data-testid={`render-job-${j.id}`}>
              {j.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> : <Loader2 className="w-4 h-4 text-indigo-600 animate-spin mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <p className="text-xs font-semibold text-zinc-800 truncate">{j.label}</p>
                <p className="text-[11px] text-zinc-500 truncate">{j.msg}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <PostVideoDialog
        project={postNowProject}
        open={!!postNowProject}
        onClose={() => setPostNowProject(null)}
        onPosted={() => setPostNowProject(null)}
      />
    </Ctx.Provider>
  );
};
